import { AlineTransforms } from '@aline/common';
import type { Editor } from 'slate';

import {
  getLiveSpellcheckRange,
  getSuggestionTargetRange,
  isLintRangeIntact,
} from './spellcheck-helpers';
import type { SpellcheckState } from './spellcheck-state-registry';
import { updateSpellcheckStateForTabId } from './spellcheck-state-registry';
import type { SpellcheckLint, SpellcheckSuggestion } from './spellcheck-vars';

interface SpellcheckActionContext {
  editor: Editor;
  focus: () => void;
  insertText: (text: string) => void;
  tabId: UUID;
}

// An `insertAfter` appends at the end of the problem; the others replace it.
export function acceptSpellcheckLint(
  context: SpellcheckActionContext,
  lint: SpellcheckLint,
  suggestion: SpellcheckSuggestion
): void {
  const { editor, focus, insertText, tabId } = context;
  const range = getLiveSpellcheckRange(lint);
  if (!range || !isLintRangeIntact(editor, lint)) return;

  // Off the list with the press, not a frame later; the edit below only asks for a fresh pass.
  updateSpellcheckStateForTabId(tabId, state => ({
    ...state,
    accepted: [...new Set([...state.accepted, lint])],
    ...withoutLints(state, [lint]),
  }));
  AlineTransforms.select(editor, getSuggestionTargetRange(range, suggestion.kind));
  insertText(suggestion.text);
  focus();
}

// Queues dismissals; the runner hands them to Harper, which stops returning those lints.
export function ignoreSpellcheckLints(
  context: SpellcheckActionContext,
  lints: readonly SpellcheckLint[]
): void {
  const { focus, tabId } = context;
  if (lints.length === 0) return;

  // Off the list with the press. Harper may only hear about it once a pass is out of the way.
  updateSpellcheckStateForTabId(tabId, state => ({
    ...state,
    ignoreRequests: [...new Set([...state.ignoreRequests, ...lints])],
    ...withoutLints(state, lints),
  }));
  focus();
}

function withoutLints(
  state: SpellcheckState,
  lints: readonly SpellcheckLint[]
): Pick<SpellcheckState, 'lints' | 'ranges'> {
  const dropped = new Set(lints);
  const droppedRanges = new Set(lints.map(lint => lint.rangeRef));
  return {
    lints: state.lints.filter(listed => !dropped.has(listed)),
    ranges: state.ranges.filter(rangeRef => !droppedRanges.has(rangeRef)),
  };
}
